import { mergeAttributes, Node, nodeInputRule } from "@tiptap/core"

export interface VideoOptions {
	HTMLAttributes: Record<string, unknown>
}

declare module "@tiptap/core" {
	interface Commands<ReturnType> {
		video: {
			setVideo: (options: { src: string; label?: string }) => ReturnType
		}
	}
}

export const inputRegex =
	/(?:^|\s)(!\[(.+|:?)]\((\S+)(?:(?:\s+)["'](\S+)["'])?\))$/

const FIRST_FRAME = "#t=0.001"

// With preload="metadata" and no poster, browsers paint nothing until play;
// a start-time fragment makes Chrome and Safari draw the first frame.
export const withFirstFrame = (src: string) =>
	src.includes("#") ? src : `${src}${FIRST_FRAME}`

export const VideoExtension = Node.create<VideoOptions>({
	name: "video",
	group: "block",
	selectable: true,
	draggable: true,
	atom: true,

	addOptions() {
		return {
			inline: false,
			HTMLAttributes: {},
		}
	},

	addAttributes() {
		return {
			src: {
				default: null,
				parseHTML: (element) =>
					element.getAttribute("src")?.replace(FIRST_FRAME, "") ?? null,
				renderHTML: (attributes) =>
					attributes.src
						? { src: withFirstFrame(attributes.src as string) }
						: {},
			},
			label: {
				default: null,
			},
			style: {
				default: "aspect-ratio:16/9;width:100%;",
				renderHTML: () => {
					return {
						style: "aspect-ratio:16/9;width:100%;",
					}
				},
			},
			controls: {
				default: true,
				renderHTML: () => {
					return {
						controls: true,
					}
				},
			},
			preload: {
				default: "metadata",
				renderHTML: () => {
					return { preload: "metadata", playsinline: "" }
				},
			},
			autoplay: {
				default: false,
				renderHTML: (attributes) => {
					// … and return an object with HTML attributes.
					if (attributes.autoplay) {
						return { autoplay: "" }
					} else {
						return {}
					}
				},
			},
			muted: {
				default: true,
				renderHTML: (attributes) => {
					// … and return an object with HTML attributes.
					if (attributes.muted) {
						return { muted: "" }
					} else {
						return {}
					}
				},
			},
		}
	},

	parseHTML() {
		return [
			{
				tag: "video[src]",
			},
		]
	},

	renderHTML({ HTMLAttributes }) {
		return [
			"video",
			mergeAttributes(this.options.HTMLAttributes, HTMLAttributes),
		]
	},

	addCommands() {
		return {
			setVideo:
				(options) =>
				({ commands }) => {
					return commands.insertContent({
						type: this.name,
						attrs: options,
					})
				},
		}
	},

	addInputRules() {
		return [
			nodeInputRule({
				find: inputRegex,
				type: this.type,
				getAttributes: (match) => {
					const [, , label, src] = match

					return {
						src,
						ariaLabel: label,
					}
				},
			}),
		]
	},
})
